/*
 * aura-taste.js: what this shopper has looked at, and what they might like (window.aura.taste).
 * Signals, the last 80 from the last 90 days in localStorage (aura.taste.v1): a product page counts 1,
 * a collection page 0.4, a try-on, sample or color chip 2, an add to cart 3. A signal counts half after
 * a week and double in the session it happened in, and the newest product counts double again, as in
 * the Explorer. The same page again within 30 minutes is the same signal. A product signal keeps the
 * collections the product is in (aura-config product.collections).
 *
 * What a color looks like comes from assets/aura-color-recs.json (scripts/nearby_colors.py): Explorer's
 * finish classifications and the color measured from its swatch. A color scores on how close it comes
 * to each color looked at, not to their average, so a shopper who looks at blues and at chromes gets both.
 *
 * Shift and Nebula colors go last in a collection (rank, sink), as in the Explorer, so the colors people
 * expect come first: a plain red before a red shift. A color is one by name, a handle with the word shift
 * or nebula (the same rule as snippets/catalog-body.liquid). They keep their place on the Shift and Nebula
 * collections and for a shopper who is after them: LATE_SHARE of what they looked at, and LATE_MIN of weight
 * (sinks()), so a single look at the Shift collection doesn't turn every collection around.
 *
 * A collection link the shopper is about to open starts that collection's pool early (window.auraPool).
 *
 * A signed-in customer's signals also live on their account, on the Marketing API (<api>/taste, AuraMarketing
 * components/taste.js), so they follow them to every device. theme.liquid signs the customer's id for it
 * (aura-config taste_sync). Once a page is shown the device's signals go up and the account's come back,
 * at most every 10 minutes with nothing new, and a page left with something new sends it as it goes. Not
 * for a shopper who declined personalization (Shopify's consent API). Another customer signing in on
 * this device starts it afresh; signing out leaves it as it is.
 *
 * Used by aura-grid.js (the catalog's For you order: rank), aura-product.js (Recommended for you: look)
 * and aura-header.js (the menu's collection links: collections). theme.liquid reads aura.taste.v1 before
 * the page paints, with the same rule as ready().
 */
(() => {
  const aura = window.aura;
  if (!aura) return;
  const cfg = aura.config || {};
  const KEY = 'aura.taste.v1';
  const MAX = 80;
  const OLDEST = 90 * 864e5;
  const HALF_LIFE = 7 * 864e5;
  const SAME = 30 * 60e3;
  const SYNC_EVERY = 10 * 60e3;
  const WEIGHTS = { product: 1, collection: 0.4, tryon: 2, sample: 2, chip: 2, cart: 3 };
  /* The distances at which a color counts e^-1 as much: CIELAB, and the finish classifications (0 to 1 each) */
  const COLOR_SPREAD = 18;
  const FINISH_SPREAD = 0.35;
  /* What keeps Shift and Nebula colors in place for a shopper: that share of their signals, and at least that much
     weight on them, as one Shift or Nebula product page this visit or two such collection pages (one, 0.8, isn't) */
  const LATE_SHARE = 0.25;
  const LATE_MIN = 1;

  let sid = null;
  try { sid = sessionStorage.getItem('aura.sid'); } catch (e) { /* private mode */ }

  /* aura.taste.v1: { e: signals, o: the customer it last synced with, y: when that sync started } */
  function load() {
    let data = null;
    try { data = JSON.parse(localStorage.getItem(KEY)); } catch (e) { /* private mode */ }
    return data && typeof data === 'object' ? data : {};
  }

  function store(list, meta = load()) {
    try { localStorage.setItem(KEY, JSON.stringify(Object.assign({}, meta, { e: list.slice(-MAX) }))); } catch (e) { /* private mode or full */ }
  }

  function read(data = load()) {
    const now = Date.now();
    return (Array.isArray(data.e) ? data.e : []).filter((s) => s && WEIGHTS[s.k] && now - s.t < OLDEST);
  }

  /* Two lists as one, oldest first: each signal once, and the same page again within SAME is one signal, the
     latest (as add() keeps them, and as the Marketing API merges) */
  function merge(a, b) {
    const now = Date.now();
    const all = new Map();
    a.concat(b).forEach((s) => all.set(`${s.k}|${s.t}|${s.h || ''}|${(s.c || []).join(',')}`, s));
    const later = new Map();
    return [...all.values()].filter((s) => s && WEIGHTS[s.k] && now - s.t < OLDEST).sort((x, y) => y.t - x.t).filter((s) => {
      const key = `${s.k}|${s.h || ''}|${(s.c || []).join(',')}`;
      const next = later.get(key);
      later.set(key, s.t);
      return next == null || next - s.t >= SAME;
    }).reverse().slice(-MAX);
  }

  const weight = (s, now) => WEIGHTS[s.k] * 0.5 ** ((now - s.t) / HALF_LIFE) * (sid && s.s === sid ? 2 : 1);
  const dist = (a, b) => {
    let d = 0;
    for (let i = 0; i < a.length; i += 1) d += (a[i] - b[i]) ** 2;
    return Math.sqrt(d);
  };

  /* kind: a WEIGHTS key. handle: the product, if any. collections: its collections (this product page's when
     left out), or the collection page's */
  function add(kind, handle, collections) {
    if (!WEIGHTS[kind]) return;
    if (!collections && handle && cfg.product && cfg.product.handle === handle) collections = cfg.product.collections;
    const c = (collections || []).filter((h) => typeof h === 'string').slice(0, 12);
    const now = Date.now();
    const signal = { k: kind, t: now, s: sid };
    if (handle) signal.h = handle;
    if (c.length) signal.c = c;
    const data = load();
    const list = read(data).filter((s) => !(s.k === kind && s.h === signal.h && String(s.c) === String(signal.c) && now - s.t < SAME));
    list.push(signal);
    store(list, data);
  }

  /* Product handle -> weight */
  function products(list = read()) {
    const now = Date.now();
    const out = new Map();
    let newest = null;
    list.forEach((s) => {
      if (!s.h) return;
      out.set(s.h, (out.get(s.h) || 0) + weight(s, now));
      if (!newest || s.t >= newest.t) newest = s;
    });
    if (newest) out.set(newest.h, out.get(newest.h) + weight(newest, now));
    return out;
  }

  /* Collection handle -> weight: the collection pages, and the collections of the products */
  function collections(list = read()) {
    const now = Date.now();
    const out = new Map();
    list.forEach((s) => (s.c || []).forEach((c) => out.set(c, (out.get(c) || 0) + weight(s, now))));
    return out;
  }

  /* Something to go on besides visits to this collection (the collection page's handle) */
  function ready(except) {
    return read().some((s) => s.h || (s.c && s.c.some((c) => c !== except)));
  }

  /* assets/aura-color-recs.json as handle -> { base, ppf, finish, lab }, fetched once. By path on this host:
     asset_url names the shop's domain, another origin on a theme preview or the local dev server */
  let rows = null;
  function table() {
    if (!rows) {
      const url = new URL(cfg.color_recs, location.href);
      rows = fetch(url.pathname + url.search)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          const n = d.keys.length;
          return new Map(d.colors.map((r) => [r[0], { base: r[1], ppf: r[2], finish: r.slice(3, 3 + n), lab: r.length >= 6 + n ? r.slice(3 + n, 6 + n) : null }]));
        });
      rows.catch(() => { rows = null; });
    }
    return rows;
  }

  /* The colors looked at, with their weights, for look() */
  function views(table, list = read()) {
    return [...products(list)].flatMap(([h, w]) => (table.has(h) ? [{ row: table.get(h), w }] : []));
  }

  /* How much a color (a table row) is like the colors looked at (views()), 0 to 1: the color itself counts
     0.55, the finish 0.45. null without a row or views */
  function look(row, seen) {
    if (!row || !seen.length) return null;
    let all = 0;
    let finish = 0;
    let lit = 0;
    let color = 0;
    seen.forEach(({ row: v, w }) => {
      all += w;
      finish += w * Math.exp(-((dist(row.finish, v.finish) / FINISH_SPREAD) ** 2));
      if (row.lab && v.lab) {
        lit += w;
        color += w * Math.exp(-((dist(row.lab, v.lab) / COLOR_SPREAD) ** 2));
      }
    });
    return lit ? 0.55 * (color / lit) + 0.45 * (finish / all) : finish / all;
  }

  const handleOf = (href) => {
    const m = /\/collections\/([^/?#]+)/.exec(new URL(href, location.origin).pathname);
    return m ? decodeURIComponent(m[1]) : null;
  };

  /* The collections the menu offers (its lists and color families): the ones that say something about a shopper */
  function offered() {
    return new Set([...document.querySelectorAll('.site-header a[href*="/collections/"]')].map((a) => handleOf(a.href)).filter(Boolean));
  }

  /* The catalog's For you order. items: [{ handle, collections, available }] in the collection's own order;
     except: this collection's handle. 70% look (above), 30% the menu collections an item shares with the
     shopper's, each counting less the more of these items are in it (all of them: not at all). The less
     there is to go on, the more the collection's own order counts. Sold-out items sink, and a color's other
     finishes give way a little, so the top isn't one color five ways. Then the Shift and Nebula colors go
     last, unless sinks() says no */
  function rank(items, table, except) {
    const list = read();
    const seen = views(table, list);
    const liked = collections(list);
    const menu = offered();
    const n = items.length;
    const count = new Map();
    items.forEach((it) => it.collections.forEach((c) => count.set(c, (count.get(c) || 0) + 1)));
    const shared = items.map((it) => it.collections.reduce((sum, c) => (
      menu.size && !menu.has(c) ? sum : sum + (liked.get(c) || 0) * Math.log((n + 1) / (count.get(c) + 1))
    ), 0));
    const most = Math.max(0, ...shared) || 1;
    const looks = items.map((it) => look(table.get(it.handle), seen));
    const known = looks.filter((l) => l != null);
    const usual = known.length ? known.reduce((a, b) => a + b, 0) / known.length : 0;
    const now = Date.now();
    const sure = Math.min(1, list.reduce((sum, s) => (s.h || (s.c && !s.c.includes(except)) ? sum + weight(s, now) : sum), 0) / 2);
    const scored = items.map((it, i) => {
      let score = sure * (0.7 * (looks[i] ?? usual) + 0.3 * (shared[i] / most)) + (0.15 + 0.3 * (1 - sure)) * (1 - i / n);
      if (!it.available) score *= 0.4;
      return { it, score, base: (table.get(it.handle) || {}).base || it.handle };
    }).sort((a, b) => b.score - a.score);
    const repeats = new Map();
    scored.forEach((x) => {
      const k = repeats.get(x.base) || 0;
      repeats.set(x.base, k + 1);
      x.score -= 0.06 * k;
    });
    const order = scored.sort((a, b) => b.score - a.score).map((x) => x.it);
    return sinks(except, list) ? sink(order) : order;
  }

  /* A Shift or Nebula color or collection, by handle */
  const isLate = (handle) => String(handle || '').split('-').some((w) => w === 'shift' || w === 'nebula');

  /* Is the shopper after Shift and Nebula colors? Their products (weighted as products()) and the other
     collection pages they opened (except: this one), LATE_SHARE of it or more on Shift and Nebula */
  function likesLate(except, list = read()) {
    const now = Date.now();
    let all = 0;
    let late = 0;
    products(list).forEach((w, h) => {
      all += w;
      if (isLate(h)) late += w;
    });
    list.forEach((s) => {
      if (s.k !== 'collection' || !s.c) return;
      s.c.forEach((c) => {
        if (c === except) return;
        const w = weight(s, now);
        all += w;
        if (isLate(c)) late += w;
      });
    });
    return late >= LATE_MIN && late / all >= LATE_SHARE;
  }

  /* Do this collection's (except) Shift and Nebula colors go last for this shopper? */
  const sinks = (except, list = read()) => !isLate(except) && !likesLate(except, list);

  /* items ([{ handle }]) with the Shift and Nebula colors after the rest, both in the order they came */
  const sink = (items) => items.filter((it) => !isLate(it.handle)).concat(items.filter((it) => isLate(it.handle)));

  /* The signed-in customer's account history (header comment), when theme.liquid signed them in for it */
  const account = cfg.taste_sync && cfg.taste_sync.token && cfg.taste_sync.url ? cfg.taste_sync : null;
  let syncAllowed = false;
  const unsynced = (data) => read(data).some((s) => s.t > (data.y || 0));
  const syncBody = () => JSON.stringify({ token: account.token, signals: read() });

  async function sync() {
    const owner = String(account.customer);
    let data = load();
    if (data.o && String(data.o) !== owner) {
      /* another customer's history leaves the device; this page's own signals stay */
      const mine = read(data).filter((s) => s.t >= performance.timeOrigin);
      data = {};
      store(mine, data);
    }
    if (String(data.o) === owner && !unsynced(data) && Date.now() - (data.y || 0) < SYNC_EVERY) return;
    const started = Date.now();
    const res = await fetch(`${account.url}/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: syncBody() });
    if (!res.ok) return;
    const { signals } = await res.json();
    if (!Array.isArray(signals)) return;
    /* signals added while it was out stay, and count as new for the next sync */
    store(merge(signals, read()), { o: owner, y: started });
  }

  /* Shopify's consent API: may this shopper's history be kept for personalization? (Yes where nothing is asked) */
  function consent() {
    return new Promise((resolve) => {
      const ask = () => {
        const privacy = window.Shopify && Shopify.customerPrivacy;
        resolve(!!privacy && (typeof privacy.preferencesProcessingAllowed !== 'function' || privacy.preferencesProcessingAllowed()));
      };
      if (window.Shopify && Shopify.customerPrivacy) ask();
      else if (window.Shopify && typeof Shopify.loadFeatures === 'function') Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => (err ? resolve(false) : ask()));
      else resolve(false);
    });
  }

  /* Deletes the account's history and this device's (for a "forget my history" control) */
  async function forget() {
    if (account) await fetch(`${account.url}/forget`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: account.token }) }).catch(() => {});
    store([], {});
  }

  if (account) {
    (window.auraShown || Promise.resolve()).then(consent).then((ok) => {
      syncAllowed = ok;
      if (!ok) return;
      const go = () => sync().catch(() => { /* next page tries again */ });
      if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 });
      else setTimeout(go, 500);
    });
    addEventListener('pagehide', () => {
      const data = load();
      if (!syncAllowed || String(data.o) !== String(account.customer) || !unsynced(data) || !navigator.sendBeacon) return;
      navigator.sendBeacon(`${account.url}/sync`, new Blob([syncBody()], { type: 'text/plain' }));
    });
  }

  aura.taste = { add, ready, products, collections, table, views, look, rank, sinks, sink, isLate, handleOf, forget };

  /* A collection link the shopper is about to open (the mouse resting on it 150 ms, or keyboard focus): when they
     get the For you order there, its pool starts now (window.auraPool, theme.liquid) so it can be in the Cache API
     by the time that page asks for it. Not on save-data, nor where the browser prerenders collection links on hover
     (theme.liquid's speculation rules with prerender_on_hover): that page starts its own pool */
  const conn = navigator.connection;
  const prerenders = document.documentElement.dataset.prerender === 'hover' && !!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
  if (window.auraPool && !(conn && conn.saveData) && !prerenders) {
    let timer = 0;
    let hovered = null;
    const linkOf = (e) => (e.target.closest ? e.target.closest('a[href*="/collections/"]:not([href*="/products/"])') : null);
    const warm = (a) => {
      const u = new URL(a.href, location.href);
      const handle = handleOf(u.href);
      if (u.origin !== location.origin || !handle || u.searchParams.has('sort_by') || Number(u.searchParams.get('page')) > 1 || !ready(handle)) return;
      u.searchParams.delete('page');
      u.searchParams.set('section_id', 'catalog-pool');
      window.auraPool(u.href).catch(() => { /* that page fetches it again */ });
    };
    document.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse') return;
      const a = linkOf(e);
      if (a === hovered) return;
      hovered = a;
      clearTimeout(timer);
      if (a) timer = setTimeout(() => warm(a), 150);
    }, { passive: true });
    document.addEventListener('focusin', (e) => {
      const a = linkOf(e);
      if (a) warm(a);
    });
  }

  /* This page, once the shopper opens it (a prerendered page may never be: theme.liquid window.auraShown) */
  (window.auraShown || Promise.resolve()).then(() => {
    if (cfg.product && cfg.product.handle) add('product', cfg.product.handle);
    else if (cfg.page_type === 'collection' && cfg.handle) add('collection', null, [cfg.handle]);
  });
})();
